import {
  AskStrikeUpButton,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Input,
  RefreshButton,
  Select,
  Stack,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useRef, useState } from "react";
import { MdAdd, MdFolder, MdQuestionAnswer, MdSearch } from "react-icons/md";
import { knowledgeBaseService } from "services/knowledgeBaseService";
import kbStatusHubService, {
  KbDocumentStatusUpdate,
} from "services/kbStatusHubService";
import { getThemeColor } from "theme/colors";
import {
  KbDocumentDto,
  KbDocumentStatus,
  KbFolderTreeDto,
  KbStatsDto,
  KbViewMode,
  KbWebSourceDto,
} from "types/knowledgeBase";
import DocumentsTable from "./components/DocumentsTable";
import MoveFolderPopover from "./components/MoveFolderPopover";
import DocumentUploader from "./components/DocumentUploader";
import FolderGroupView from "./components/FolderGroupView";
import KbStorageUsageCard from "./components/KbStorageUsageCard";
import KbFolderTree, {
  UNCATEGORIZED_FOLDER_ID,
} from "./components/KbFolderTree";
import TestRetrievalModal from "./components/TestRetrievalModal";
import WebSourceModal from "./components/WebSourceModal";
import WebSourcesTable from "./components/WebSourcesTable";

type AddChoice = "choice" | "document" | "webSource" | null;

const KnowledgeBase = () => {
  const { t } = useTranslation();

  // Folder selection
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [folderTree, setFolderTree] = useState<KbFolderTreeDto[]>([]);

  // Documents state
  const [documents, setDocuments] = useState<KbDocumentDto[]>([]);
  const [stats, setStats] = useState<KbStatsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | undefined>(undefined);
  const [isRefreshing, setIsRefreshing] = useState<string | undefined>(
    undefined,
  );

  const statsRefreshTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Multiselect — documents
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Multiselect — web sources
  const [selectedWsIds, setSelectedWsIds] = useState<Set<string>>(new Set());
  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectedWsIds(new Set());
  };

  // Search / filter / pagination
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  // Web sources state
  const [webSources, setWebSources] = useState<KbWebSourceDto[]>([]);
  const [webSourcesLoading, setWebSourcesLoading] = useState(false);
  const [webSourcesError, setWebSourcesError] = useState<string | null>(null);
  const [isWebSourceModalOpen, setIsWebSourceModalOpen] = useState(false);
  const [editingWebSource, setEditingWebSource] =
    useState<KbWebSourceDto | null>(null);
  const [isDeletingWebSource, setIsDeletingWebSource] = useState<
    string | undefined
  >(undefined);
  const [isSyncingWebSource, setIsSyncingWebSource] = useState<
    string | undefined
  >(undefined);

  // Modals
  const [addChoice, setAddChoice] = useState<AddChoice>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isTestRetrievalOpen, setIsTestRetrievalOpen] = useState(false);

  // Which "section" to show in the right panel
  const [viewMode, setViewMode] = useState<KbViewMode>("all");

  // Incremented to signal FolderGroupView cards to reload their lazy-loaded contents
  const [folderGroupRefreshToken, setFolderGroupRefreshToken] = useState(0);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const status =
        statusFilter !== "all" ? (statusFilter as KbDocumentStatus) : undefined;
      const isUncategorized = selectedFolderId === UNCATEGORIZED_FOLDER_ID;
      const [docsResult, statsData] = await Promise.all([
        knowledgeBaseService.getDocuments({
          search: search || undefined,
          status,
          page: isUncategorized ? 1 : page,
          pageSize: isUncategorized ? 500 : pageSize,
          folderId: isUncategorized ? undefined : selectedFolderId,
        }),
        knowledgeBaseService.getStats(),
      ]);
      const items = isUncategorized
        ? docsResult.items.filter((d) => !d.folderId)
        : docsResult.items;
      setDocuments(items);
      setTotal(isUncategorized ? items.length : docsResult.total);
      setStats(statsData);
      setError(null);
      setSelectedIds(new Set());
    } catch (err: any) {
      setError(
        err.response?.data?.message || t("app.knowledgeBase.loadFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const loadWebSources = async () => {
    try {
      setWebSourcesLoading(true);
      const isUncategorized = selectedFolderId === UNCATEGORIZED_FOLDER_ID;
      const sources = await knowledgeBaseService.getWebSources(
        isUncategorized ? null : selectedFolderId,
      );
      const wsItems = isUncategorized
        ? sources.filter((ws) => !ws.folderId)
        : sources;
      setWebSources(wsItems);
      setWebSourcesError(null);
    } catch (err: any) {
      setWebSourcesError(
        err.response?.data?.message ||
          t("app.knowledgeBase.webSources.loadFailed"),
      );
    } finally {
      setWebSourcesLoading(false);
    }
  };

  // Reload both when selection / filters change
  useEffect(() => {
    knowledgeBaseService.getFolderTree().then(setFolderTree);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDocuments();
    loadWebSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter, selectedFolderId]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleStatusFilterChange = (value: string[]) => {
    setStatusFilter(value[0] || "all");
    setPage(1);
  };

  const handleFolderSelect = (id: string | null) => {
    setSelectedFolderId(id);
    setPage(1);
  };

  // Add choice handler
  const handleAddChoice = (choice: "document" | "webSource") => {
    setAddChoice(null);
    if (choice === "document") setIsUploadModalOpen(true);
    else {
      setEditingWebSource(null);
      setIsWebSourceModalOpen(true);
    }
  };

  const handleDelete = async (docId: string) => {
    if (!window.confirm(t("app.knowledgeBase.delete.confirm"))) return;
    try {
      setIsDeleting(docId);
      await knowledgeBaseService.deleteDocument(docId);
      await loadDocuments();
    } catch (err: any) {
      setError(
        err.response?.data?.message || t("app.knowledgeBase.delete.failed"),
      );
    } finally {
      setIsDeleting(undefined);
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleToggleAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(documents.map((d) => d.id)));
  };

  const handleFolderToggleAll = (docIds: string[], checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (!checked) {
        docIds.forEach((id) => next.delete(id));
      } else {
        docIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleWsToggleSelect = (id: string) => {
    setSelectedWsIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleWsToggleAll = (checked: boolean) => {
    if (!checked) {
      setSelectedWsIds(new Set());
      return;
    }
    setSelectedWsIds(new Set(webSources.map((ws) => ws.id)));
  };

  const handleFolderWsToggleAll = (wsIds: string[], checked: boolean) => {
    setSelectedWsIds((prev) => {
      const next = new Set(prev);
      if (!checked) {
        wsIds.forEach((id) => next.delete(id));
      } else {
        wsIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleBulkWsDelete = async () => {
    if (
      !window.confirm(
        t("app.knowledgeBase.bulk.confirmDelete", {
          count: selectedWsIds.size,
        }),
      )
    )
      return;
    try {
      await knowledgeBaseService.bulkDeleteWebSources([...selectedWsIds]);
      setSelectedWsIds(new Set());
      await loadWebSources();
      setFolderGroupRefreshToken((t) => t + 1);
    } catch (err: any) {
      setError(
        err.response?.data?.message || t("app.knowledgeBase.delete.failed"),
      );
    }
  };

  const handleBulkWsMove = async (folderId: string | null) => {
    try {
      await knowledgeBaseService.bulkMoveWebSources(
        [...selectedWsIds],
        folderId,
      );
      setSelectedWsIds(new Set());
      const tree = await knowledgeBaseService.getFolderTree();
      setFolderTree(tree);
      await loadWebSources();
      setFolderGroupRefreshToken((tok) => tok + 1);
    } catch (err: any) {
      setError(
        err.response?.data?.message || t("app.knowledgeBase.delete.failed"),
      );
    }
  };

  const handleBulkWsSetGlobal = (isGlobal: boolean) => async () => {
    try {
      await knowledgeBaseService.bulkSetGlobalWebSources(
        [...selectedWsIds],
        isGlobal,
      );
      setSelectedWsIds(new Set());
      await loadWebSources();
      setFolderGroupRefreshToken((t) => t + 1);
    } catch (err: any) {
      setError(
        err.response?.data?.message || t("app.knowledgeBase.delete.failed"),
      );
    }
  };

  const handleBulkDelete = async () => {
    if (
      !window.confirm(
        t("app.knowledgeBase.bulk.confirmDelete", { count: selectedIds.size }),
      )
    )
      return;
    try {
      await knowledgeBaseService.bulkDeleteDocuments([...selectedIds]);
      clearSelection();
      await loadDocuments();
      setFolderGroupRefreshToken((t) => t + 1);
    } catch (err: any) {
      setError(
        err.response?.data?.message || t("app.knowledgeBase.delete.failed"),
      );
    }
  };

  const handleBulkMove = async (folderId: string | null) => {
    try {
      await knowledgeBaseService.bulkMoveDocuments([...selectedIds], folderId);
      clearSelection();
      const tree = await knowledgeBaseService.getFolderTree();
      setFolderTree(tree);
      await loadDocuments();
      setFolderGroupRefreshToken((tok) => tok + 1);
    } catch (err: any) {
      setError(
        err.response?.data?.message || t("app.knowledgeBase.delete.failed"),
      );
    }
  };

  const handleBulkSetGlobal = (isGlobal: boolean) => async () => {
    try {
      await knowledgeBaseService.bulkSetGlobalDocuments(
        [...selectedIds],
        isGlobal,
      );
      clearSelection();
      await loadDocuments();
      setFolderGroupRefreshToken((t) => t + 1);
    } catch (err: any) {
      setError(
        err.response?.data?.message || t("app.knowledgeBase.delete.failed"),
      );
    }
  };

  const handleRefreshStatus = async (docId: string) => {
    try {
      setIsRefreshing(docId);
      const [status, statsData] = await Promise.all([
        knowledgeBaseService.getDocumentStatus(docId),
        knowledgeBaseService.getStats(),
      ]);
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === docId
            ? {
                ...doc,
                status: status.status,
                errorMessage: status.errorMessage,
              }
            : doc,
        ),
      );
      setStats(statsData);
    } catch {
      /* silent */
    } finally {
      setIsRefreshing(undefined);
    }
  };

  // Subscribe to real-time KB document status updates via SignalR.
  // The backend KbIngestionPollingService polls Bedrock and broadcasts on transition.
  useEffect(() => {
    const handleStatusChange = (update: KbDocumentStatusUpdate) => {
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === update.docId
            ? {
                ...doc,
                status: update.status,
                errorMessage: update.errorMessage,
              }
            : doc,
        ),
      );
      // Debounce: batch rapid events (e.g. bulk upload) into a single stats fetch
      if (statsRefreshTimeout.current)
        clearTimeout(statsRefreshTimeout.current);
      statsRefreshTimeout.current = setTimeout(async () => {
        try {
          const statsData = await knowledgeBaseService.getStats();
          setStats(statsData);
        } catch {
          /* silent */
        }
      }, 500);
    };

    kbStatusHubService.onStatusChanged(handleStatusChange);
    kbStatusHubService.subscribe().catch(() => {});

    return () => {
      kbStatusHubService.offStatusChanged(handleStatusChange);
      kbStatusHubService.unsubscribe().catch(() => {});
      if (statsRefreshTimeout.current)
        clearTimeout(statsRefreshTimeout.current);
    };
  }, []);

  const handleEditWebSource = (source: KbWebSourceDto) => {
    setEditingWebSource(source);
    setIsWebSourceModalOpen(true);
  };

  const handleDeleteWebSource = async (id: string) => {
    if (!window.confirm(t("app.knowledgeBase.webSources.delete.confirm")))
      return;
    try {
      setIsDeletingWebSource(id);
      await knowledgeBaseService.deleteWebSource(id);
      await loadWebSources();
    } catch (err: any) {
      setWebSourcesError(
        err.response?.data?.message ||
          t("app.knowledgeBase.webSources.delete.failed"),
      );
    } finally {
      setIsDeletingWebSource(undefined);
    }
  };

  const handleSyncWebSource = async (id: string) => {
    try {
      setIsSyncingWebSource(id);
      const updated = await knowledgeBaseService.syncWebSource(id);
      setWebSources((prev) => prev.map((s) => (s.id === id ? updated : s)));
    } catch (err: any) {
      setWebSourcesError(
        err.response?.data?.message ||
          t("app.knowledgeBase.webSources.sync.failed"),
      );
    } finally {
      setIsSyncingWebSource(undefined);
    }
  };

  const handleWebSourceModalClose = () => {
    setIsWebSourceModalOpen(false);
    setEditingWebSource(null);
  };

  const handleWebSourceSuccess = async () => {
    await loadWebSources();
  };

  const handleSetFolderGlobal = async (id: string, isGlobal: boolean) => {
    await knowledgeBaseService.setFolderGlobal(id, isGlobal);
    const tree = await knowledgeBaseService.getFolderTree();
    setFolderTree(tree);
    // Reload flat lists so isGlobal badges update on all items
    await Promise.all([loadDocuments(), loadWebSources()]);
    setFolderGroupRefreshToken((t) => t + 1);
  };

  const handleMove = async (
    type: "document" | "webSource",
    id: string,
    folderId: string | null,
  ) => {
    if (type === "document")
      await knowledgeBaseService.moveDocument(id, { folderId });
    else await knowledgeBaseService.moveWebSource(id, { folderId });
    const tree = await knowledgeBaseService.getFolderTree();
    setFolderTree(tree);
    await (type === "document" ? loadDocuments() : loadWebSources());
    setFolderGroupRefreshToken((t) => t + 1);
  };

  const totalPages = Math.ceil(total / pageSize);
  const showDocs = viewMode === "all" || viewMode === "documents";
  const showWebSources = viewMode === "all" || viewMode === "webSources";

  const isRootAllView = selectedFolderId === null && viewMode === "all";
  const visibleDocuments = documents;
  const visibleWebSources = webSources;

  // Flat folder list for MoveFolderPopover (root folders + children with parent name)
  const flatFolders = folderTree.flatMap((root) => [
    { id: root.id, name: root.name },
    ...root.children.map((child) => ({
      id: child.id,
      name: child.name,
      parentName: root.name,
    })),
  ]);

  return (
    <Stack gap={4} w="full" maxW="full">
      {/* Header */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Heading>{t("app.knowledgeBase.title")}</Heading>
        <HStack gap={2}>
          <AskStrikeUpButton
            context={t("app.askStrikeUp.context.knowledgeBase")}
          />
          <RefreshButton
            onClick={() => {
              loadDocuments();
              loadWebSources();
            }}
            loading={loading || webSourcesLoading}
          />
          <Button
            intent="secondary"
            onClick={() => setIsTestRetrievalOpen(true)}
            disabled={
              !stats ||
              (stats.indexedDocuments === 0 && stats.activeWebSources === 0)
            }
          >
            <MdQuestionAnswer />
            {t("app.knowledgeBase.testRetrieval.button")}
          </Button>
          <Button onClick={() => setAddChoice("choice")} intent="primary">
            <MdAdd />
            Add to KB
          </Button>
        </HStack>
      </Flex>

      {/* Stats */}
      {stats && (
        <HStack gap={4} wrap="wrap">
          <Card.Root flex="1" minW="150px">
            <Card.Body>
              <VStack align="start" gap={1}>
                <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                  {t("app.knowledgeBase.stats.total")}
                </Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {stats.totalDocuments + stats.totalWebSources}
                </Text>
                <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                  {stats.totalDocuments} docs, {stats.totalWebSources} web
                  sources
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root>
          <Card.Root flex="1" minW="150px">
            <Card.Body>
              <VStack align="start" gap={1}>
                <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                  {t("app.knowledgeBase.stats.indexed")}
                </Text>
                <HStack>
                  <Text fontSize="2xl" fontWeight="bold" color="green.500">
                    {stats.indexedDocuments + stats.activeWebSources}
                  </Text>
                  <Badge colorPalette="green">
                    {t("app.knowledgeBase.status.indexed")}
                  </Badge>
                </HStack>
                <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                  {stats.indexedDocuments} docs, {stats.activeWebSources} web
                  sources
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root>
          <Card.Root flex="1" minW="150px">
            <Card.Body>
              <VStack align="start" gap={1}>
                <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                  {t("app.knowledgeBase.stats.processing")}
                </Text>
                <HStack>
                  <Text fontSize="2xl" fontWeight="bold" color="yellow.500">
                    {stats.processingDocuments + stats.syncingWebSources}
                  </Text>
                  {stats.processingDocuments + stats.syncingWebSources > 0 && (
                    <Badge colorPalette="yellow">
                      {t("app.knowledgeBase.status.processing")}
                    </Badge>
                  )}
                </HStack>
              </VStack>
            </Card.Body>
          </Card.Root>
          <KbStorageUsageCard stats={stats} />
          {stats.failedDocuments + stats.failedWebSources > 0 && (
            <Card.Root flex="1" minW="150px">
              <Card.Body>
                <VStack align="start" gap={1}>
                  <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                    {t("app.knowledgeBase.stats.failed")}
                  </Text>
                  <HStack>
                    <Text fontSize="2xl" fontWeight="bold" color="red.500">
                      {stats.failedDocuments + stats.failedWebSources}
                    </Text>
                    <Badge colorPalette="red">
                      {t("app.knowledgeBase.status.failed")}
                    </Badge>
                  </HStack>
                </VStack>
              </Card.Body>
            </Card.Root>
          )}
        </HStack>
      )}

      {/* Main layout: folder tree + content */}
      <HStack align="start" gap={4}>
        {/* Folder sidebar */}
        <Card.Root w="220px" flexShrink={0} position="sticky" top={4}>
          <Card.Body p={3} overflowY="auto" maxH="calc(100vh - 200px)">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              textTransform="uppercase"
              letterSpacing="wide"
              color={getThemeColor("text.secondary")}
              mb={2}
            >
              Folders
            </Text>
            <KbFolderTree
              selectedFolderId={selectedFolderId}
              onSelect={handleFolderSelect}
              onMutated={() => {
                loadDocuments();
                loadWebSources();
                knowledgeBaseService.getFolderTree().then(setFolderTree);
              }}
              onSetFolderGlobal={handleSetFolderGlobal}
              onDropDocument={(id, fid) => handleMove("document", id, fid)}
              onDropWebSource={(id, fid) => handleMove("webSource", id, fid)}
              refreshToken={folderGroupRefreshToken}
            />
          </Card.Body>
        </Card.Root>

        {/* Right panel */}
        <Stack flex={1} gap={4} minW={0}>
          {/* View mode toggle + search/filter */}
          <Card.Root>
            <Card.Body>
              <HStack gap={3} wrap="wrap">
                {/* Segment: All / Documents / Web Sources */}
                <HStack
                  gap={0}
                  border="1px solid"
                  borderColor={getThemeColor("border.default")}
                  borderRadius="md"
                  overflow="hidden"
                >
                  {(["all", "documents", "webSources"] as KbViewMode[]).map(
                    (mode) => (
                      <Button
                        key={mode}
                        size="sm"
                        variant={viewMode === mode ? "solid" : "ghost"}
                        colorPalette={viewMode === mode ? "blue" : "gray"}
                        borderRadius={0}
                        onClick={() => setViewMode(mode)}
                        px={3}
                      >
                        {mode === "all"
                          ? "All"
                          : mode === "documents"
                            ? "Documents"
                            : "Web Sources"}
                      </Button>
                    ),
                  )}
                </HStack>

                {(viewMode === "all" || viewMode === "documents") && (
                  <>
                    <Input
                      placeholder={t("app.knowledgeBase.search.placeholder")}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                      w="280px"
                    />
                    <Button onClick={handleSearch} intent="primary" size="sm">
                      <MdSearch />
                    </Button>
                    <Select
                      value={[statusFilter]}
                      onValueChange={(details) =>
                        handleStatusFilterChange(details.value)
                      }
                      items={[
                        {
                          label: t("app.knowledgeBase.filter.allStatuses"),
                          value: "all",
                        },
                        {
                          label: t("app.knowledgeBase.status.uploaded"),
                          value: KbDocumentStatus.Uploaded,
                        },
                        {
                          label: t("app.knowledgeBase.status.processing"),
                          value: KbDocumentStatus.Processing,
                        },
                        {
                          label: t("app.knowledgeBase.status.indexed"),
                          value: KbDocumentStatus.Indexed,
                        },
                        {
                          label: t("app.knowledgeBase.status.failed"),
                          value: KbDocumentStatus.Failed,
                        },
                      ]}
                      w="180px"
                    />
                  </>
                )}
              </HStack>
            </Card.Body>
          </Card.Root>

          {/* Error */}
          {(error || webSourcesError) && (
            <Card.Root
              bg={getThemeColor("alert.error.bg")}
              borderColor={getThemeColor("alert.error.border")}
            >
              <Card.Body>
                <Text color={getThemeColor("alert.error.text")}>
                  {error || webSourcesError}
                </Text>
              </Card.Body>
            </Card.Root>
          )}

          {/* Bulk action bar — documents */}
          {selectedIds.size > 0 && (
            <HStack
              px={3}
              py={2}
              gap={2}
              flexWrap="wrap"
              borderRadius="md"
              bg={{ base: "blue.50", _dark: "blue.900" }}
              borderWidth="1px"
              borderColor={{ base: "blue.200", _dark: "blue.700" }}
            >
              <Text
                fontSize="sm"
                fontWeight="medium"
                color={{ base: "blue.700", _dark: "blue.200" }}
              >
                {selectedIds.size} document{selectedIds.size !== 1 ? "s" : ""}{" "}
                selected
              </Text>
              <Button
                size="xs"
                intent="ghost"
                onClick={handleBulkSetGlobal(true)}
              >
                {t("app.knowledgeBase.bulk.setGlobal")}
              </Button>
              <Button
                size="xs"
                intent="ghost"
                onClick={handleBulkSetGlobal(false)}
              >
                {t("app.knowledgeBase.bulk.unsetGlobal")}
              </Button>
              <HStack gap={1}>
                <Text
                  fontSize="sm"
                  color={{ base: "blue.700", _dark: "blue.200" }}
                >
                  {t("app.knowledgeBase.bulk.moveSelected")}:
                </Text>
                <MoveFolderPopover
                  folders={flatFolders}
                  onMove={handleBulkMove}
                />
              </HStack>
              <Button size="xs" intent="danger" onClick={handleBulkDelete}>
                {t("app.knowledgeBase.bulk.deleteSelected")}
              </Button>
              <Button
                size="xs"
                intent="ghost"
                onClick={() => setSelectedIds(new Set())}
              >
                {t("app.knowledgeBase.bulk.clearSelection")}
              </Button>
            </HStack>
          )}

          {/* Bulk action bar — web sources */}
          {selectedWsIds.size > 0 && (
            <HStack
              px={3}
              py={2}
              gap={2}
              flexWrap="wrap"
              borderRadius="md"
              bg={{ base: "purple.50", _dark: "purple.900" }}
              borderWidth="1px"
              borderColor={{ base: "purple.200", _dark: "purple.700" }}
            >
              <Text
                fontSize="sm"
                fontWeight="medium"
                color={{ base: "purple.700", _dark: "purple.200" }}
              >
                {selectedWsIds.size} web source
                {selectedWsIds.size !== 1 ? "s" : ""} selected
              </Text>
              <Button
                size="xs"
                intent="ghost"
                onClick={handleBulkWsSetGlobal(true)}
              >
                {t("app.knowledgeBase.bulk.setGlobal")}
              </Button>
              <Button
                size="xs"
                intent="ghost"
                onClick={handleBulkWsSetGlobal(false)}
              >
                {t("app.knowledgeBase.bulk.unsetGlobal")}
              </Button>
              <HStack gap={1}>
                <Text
                  fontSize="sm"
                  color={{ base: "purple.700", _dark: "purple.200" }}
                >
                  {t("app.knowledgeBase.bulk.moveSelected")}:
                </Text>
                <MoveFolderPopover
                  folders={flatFolders}
                  onMove={handleBulkWsMove}
                />
              </HStack>
              <Button size="xs" intent="danger" onClick={handleBulkWsDelete}>
                {t("app.knowledgeBase.bulk.deleteSelected")}
              </Button>
              <Button
                size="xs"
                intent="ghost"
                onClick={() => setSelectedWsIds(new Set())}
              >
                {t("app.knowledgeBase.bulk.clearSelection")}
              </Button>
            </HStack>
          )}

          {/* Grouped view — when "All items" is selected (no folder filter) */}
          {selectedFolderId === null &&
            viewMode !== "documents" &&
            viewMode !== "webSources" &&
            folderTree.length > 0 && (
              <FolderGroupView
                folderTree={folderTree}
                viewMode={viewMode}
                onFolderNavigate={handleFolderSelect}
                onDelete={handleDelete}
                onRefreshStatus={handleRefreshStatus}
                onDocumentUpdated={(updated) =>
                  setDocuments((prev) =>
                    prev.map((d) => (d.id === updated.id ? updated : d)),
                  )
                }
                onEditWebSource={handleEditWebSource}
                onDeleteWebSource={handleDeleteWebSource}
                onSyncWebSource={handleSyncWebSource}
                onWebSourceUpdated={(updated) =>
                  setWebSources((prev) =>
                    prev.map((s) => (s.id === updated.id ? updated : s)),
                  )
                }
                onMove={handleMove}
                onSetFolderGlobal={handleSetFolderGlobal}
                onAddToKb={() => setAddChoice("choice")}
                folders={flatFolders}
                refreshToken={folderGroupRefreshToken}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onFolderToggleAll={handleFolderToggleAll}
                selectedWsIds={selectedWsIds}
                onToggleWsSelect={handleWsToggleSelect}
                onFolderWsToggleAll={handleFolderWsToggleAll}
              />
            )}

          {/* Documents section */}
          {showDocs && (loading || visibleDocuments.length > 0) && (
            <Card.Root>
              <Card.Body>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  mb={3}
                  color={getThemeColor("text.secondary")}
                >
                  Documents
                  {loading ? "" : ` (${total})`}
                </Text>
                {loading ? (
                  <Flex justify="center" align="center" minH="120px">
                    <Text>{t("app.common.loading")}</Text>
                  </Flex>
                ) : visibleDocuments.length === 0 ? (
                  <Flex
                    justify="center"
                    align="center"
                    minH="100px"
                    direction="column"
                    gap={2}
                  >
                    <Text color={getThemeColor("text.secondary")}>
                      {t("app.knowledgeBase.noDocuments")}
                    </Text>
                    <Button
                      size="sm"
                      intent="secondary"
                      onClick={() => setIsUploadModalOpen(true)}
                    >
                      <MdAdd /> Upload document
                    </Button>
                  </Flex>
                ) : (
                  <>
                    <DocumentsTable
                      documents={visibleDocuments}
                      onDelete={handleDelete}
                      onRefreshStatus={handleRefreshStatus}
                      onDocumentUpdated={(updated) => {
                        setDocuments((prev) =>
                          prev.map((d) => (d.id === updated.id ? updated : d)),
                        );
                      }}
                      isDeleting={isDeleting}
                      isRefreshing={isRefreshing}
                      onMove={(id, fid) => handleMove("document", id, fid)}
                      folders={flatFolders}
                      selectedIds={selectedIds}
                      onToggleSelect={handleToggleSelect}
                      onToggleAll={handleToggleAll}
                    />
                    {total > pageSize && (
                      <HStack justify="space-between" mt={3}>
                        <Text
                          fontSize="sm"
                          color={{ base: "gray.600", _dark: "gray.400" }}
                        >
                          {(page - 1) * pageSize + 1}–
                          {Math.min(page * pageSize, total)} of {total}
                        </Text>
                        <HStack>
                          <Button
                            onClick={() => setPage(page - 1)}
                            disabled={page === 1}
                            size="sm"
                          >
                            {t("app.common.previous")}
                          </Button>
                          <Text fontSize="sm">
                            {page} / {totalPages}
                          </Text>
                          <Button
                            onClick={() => setPage(page + 1)}
                            disabled={page >= totalPages}
                            size="sm"
                          >
                            {t("app.common.next")}
                          </Button>
                        </HStack>
                      </HStack>
                    )}
                  </>
                )}
              </Card.Body>
            </Card.Root>
          )}

          {/* Web sources section */}
          {showWebSources &&
            (webSourcesLoading || visibleWebSources.length > 0) && (
              <Card.Root>
                <Card.Body>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    mb={3}
                    color={getThemeColor("text.secondary")}
                  >
                    Web Sources
                    {webSourcesLoading ? "" : ` (${visibleWebSources.length})`}
                  </Text>
                  {webSourcesLoading ? (
                    <Flex justify="center" align="center" minH="120px">
                      <Text>{t("app.common.loading")}</Text>
                    </Flex>
                  ) : visibleWebSources.length === 0 ? (
                    <Flex
                      justify="center"
                      align="center"
                      minH="100px"
                      direction="column"
                      gap={2}
                    >
                      <Text color={getThemeColor("text.secondary")}>
                        No web sources yet
                      </Text>
                      <Button
                        size="sm"
                        intent="secondary"
                        onClick={() => {
                          setEditingWebSource(null);
                          setIsWebSourceModalOpen(true);
                        }}
                      >
                        <MdAdd /> Add web source
                      </Button>
                    </Flex>
                  ) : (
                    <WebSourcesTable
                      webSources={visibleWebSources}
                      onEdit={handleEditWebSource}
                      onDelete={handleDeleteWebSource}
                      onSync={handleSyncWebSource}
                      onWebSourceUpdated={(updated) => {
                        setWebSources((prev) =>
                          prev.map((s) => (s.id === updated.id ? updated : s)),
                        );
                      }}
                      isDeleting={isDeletingWebSource}
                      isSyncing={isSyncingWebSource}
                      onMove={(id, fid) => handleMove("webSource", id, fid)}
                      folders={flatFolders}
                      selectedIds={selectedWsIds}
                      onToggleSelect={handleWsToggleSelect}
                      onToggleAll={handleWsToggleAll}
                    />
                  )}
                </Card.Body>
              </Card.Root>
            )}
        </Stack>
      </HStack>

      {/* Add choice inline prompt (simple, no extra modal needed) */}
      {addChoice === "choice" && (
        <Flex
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={1000}
          align="center"
          justify="center"
          bg="blackAlpha.600"
          onClick={() => setAddChoice(null)}
        >
          <Box
            maxW="400px"
            w="full"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <Card.Root w="full">
              <Card.Header>
                <Heading size="md">Add to Knowledge Base</Heading>
              </Card.Header>
              <Card.Body>
                <VStack gap={3}>
                  <Button
                    w="full"
                    intent="primary"
                    onClick={() => handleAddChoice("document")}
                  >
                    <MdAdd /> Upload Document
                  </Button>
                  <Button
                    w="full"
                    intent="secondary"
                    onClick={() => handleAddChoice("webSource")}
                  >
                    <MdAdd /> Add Web Source
                  </Button>
                </VStack>
              </Card.Body>
              <Card.Footer>
                <Button
                  intent="ghost"
                  w="full"
                  onClick={() => setAddChoice(null)}
                >
                  Cancel
                </Button>
              </Card.Footer>
            </Card.Root>
          </Box>
        </Flex>
      )}

      <DocumentUploader
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadComplete={loadDocuments}
        remainingStorageBytes={
          stats
            ? Math.max(0, stats.storageQuotaBytes - stats.storageUsedBytes)
            : undefined
        }
        defaultFolderId={
          selectedFolderId && selectedFolderId !== UNCATEGORIZED_FOLDER_ID
            ? selectedFolderId
            : undefined
        }
      />

      <TestRetrievalModal
        isOpen={isTestRetrievalOpen}
        onClose={() => setIsTestRetrievalOpen(false)}
      />

      <WebSourceModal
        isOpen={isWebSourceModalOpen}
        onClose={handleWebSourceModalClose}
        onSuccess={handleWebSourceSuccess}
        editingSource={editingWebSource}
        defaultFolderId={
          selectedFolderId && selectedFolderId !== UNCATEGORIZED_FOLDER_ID
            ? selectedFolderId
            : undefined
        }
      />
    </Stack>
  );
};

export default KnowledgeBase;
